import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  clearPendingImages,
  getPendingImageFiles,
  hasPendingImages,
  replaceBlobUrlsInString,
} from './EditableMarkdown';
import {
  arrayBufferToBase64,
  clearToken,
  commitAll,
  deleteFile as ghDeleteFile,
  getFileContent,
  getStoredToken,
  initiateLogin,
  validateToken,
} from './github';
import type { FileChange } from './github';
import type { PendingChange } from './types';

type EditContextValue = {
  isAuthenticated: boolean;
  isEditMode: boolean;
  isSaving: boolean;
  pendingChanges: Map<string, PendingChange>;
  token: string | null;
  login: () => Promise<void>;
  logout: () => void;
  toggleEditMode: () => void;
  registerChange: (key: string, change: PendingChange) => void;
  saveAll: () => Promise<void>;
  discardAll: () => void;
  toast: string | null;
};

const EditContext = createContext<EditContextValue>({
  isAuthenticated: false,
  isEditMode: false,
  isSaving: false,
  pendingChanges: new Map(),
  token: null,
  login: async () => {},
  logout: () => {},
  toggleEditMode: () => {},
  registerChange: () => {},
  saveAll: async () => {},
  discardAll: () => {},
  toast: null,
});

export function useEditContext() {
  return useContext(EditContext);
}

export function useIsEditing() {
  const { isEditMode, isAuthenticated } = useContext(EditContext);
  return isEditMode && isAuthenticated;
}

export function EditProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingChange>>(
    new Map()
  );
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Validate stored token on mount
  useEffect(() => {
    const stored = getStoredToken();
    if (stored) {
      validateToken(stored).then((valid) => {
        if (valid) {
          setToken(stored);
          setIsAuthenticated(true);
        } else {
          clearToken();
        }
      });
    }
  }, []);

  // Check URL param for edit mode
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('edit') === 'true' && isAuthenticated) {
      setIsEditMode(true);
    }
  }, [isAuthenticated]);

  const login = useCallback(async () => {
    try {
      const newToken = await initiateLogin();
      setToken(newToken);
      setIsAuthenticated(true);
      showToast('Sisse logitud');
    } catch (e) {
      if ((e as Error).message !== 'Login cancelled') {
        showToast('Sisselogimine ebaõnnestus');
      }
    }
  }, [showToast]);

  const logout = useCallback(() => {
    clearToken();
    setToken(null);
    setIsAuthenticated(false);
    setIsEditMode(false);
    setPendingChanges(new Map());
  }, []);

  const toggleEditMode = useCallback(() => {
    setIsEditMode((prev) => {
      if (prev && pendingChanges.size > 0) {
        // Exiting edit mode with unsaved changes - warn
        if (!window.confirm('Discard unsaved changes?')) return true;
        setPendingChanges(new Map());
      }
      return !prev;
    });
  }, [pendingChanges]);

  const registerChange = useCallback((key: string, change: PendingChange) => {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.set(key, change);
      return next;
    });
  }, []);

  const saveAll = useCallback(async () => {
    if (!token || pendingChanges.size === 0) return;

    setIsSaving(true);
    try {
      const changes: FileChange[] = [];

      // 1. Resolve pending images to file data + paths (populates blobToPathMap)
      if (hasPendingImages()) {
        const imageFiles = getPendingImageFiles();
        for (const { path, file } of imageFiles) {
          const buffer = await file.arrayBuffer();
          changes.push({
            path,
            content: arrayBufferToBase64(buffer),
            encoding: 'base64',
          });
        }
      }

      // 2. Collect text file changes (with blob URLs replaced)
      for (const [, change] of pendingChanges) {
        if (change.isDeleted) {
          changes.push({
            path: change.filePath,
            content: '',
            encoding: 'utf-8',
            deleted: true,
          });
          continue;
        }

        const body = replaceBlobUrlsInString(change.body);
        changes.push({
          path: change.filePath,
          content: body,
          encoding: 'utf-8',
        });
      }

      // 3. Single atomic commit
      const changedPaths = changes
        .map((c) => c.path.split('/').pop())
        .join(', ');
      await commitAll(changes, `Update ${changedPaths}`, token);

      setPendingChanges(new Map());
      clearPendingImages();
      showToast(`${changes.length} fail(i) salvestatud. Leht uueneb ~1 minutiga.`);
    } catch (e) {
      showToast(`Salvestamine ebaõnnestus: ${(e as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  }, [token, pendingChanges, showToast]);

  const discardAll = useCallback(() => {
    setPendingChanges(new Map());
    clearPendingImages();
    setIsEditMode(false);
    setTimeout(() => setIsEditMode(true), 0);
    showToast('Muudatused tühistatud');
  }, [showToast]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isEditMode,
      isSaving,
      pendingChanges,
      token,
      login,
      logout,
      toggleEditMode,
      registerChange,
      saveAll,
      discardAll,
      toast,
    }),
    [
      isAuthenticated,
      isEditMode,
      isSaving,
      pendingChanges,
      token,
      login,
      logout,
      toggleEditMode,
      registerChange,
      saveAll,
      discardAll,
      toast,
    ]
  );

  return <EditContext.Provider value={value}>{children}</EditContext.Provider>;
}
